import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clock, X, Keyboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '../UI/dialog';
import { Button } from '../UI/button';
import { Label } from '../UI/label';
import { cn } from '../../Lib/Cn';

const TimePickerDialog = ({ open, onOpenChange, value, onChange, fieldId, label, date = null }) => {
  const [selectedHour, setSelectedHour] = useState(6);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [ampm, setAmpm] = useState('AM');
  const [mode, setMode] = useState('hours'); // 'hours' | 'minutes'
  const [inputMode, setInputMode] = useState('clock'); // 'clock' | 'keyboard'
  const [inputHourStr, setInputHourStr] = useState('6');
  const [inputMinuteStr, setInputMinuteStr] = useState('00');
  const [activeInput, setActiveInput] = useState('hours'); // 'hours' | 'minutes'

  const clockRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Parse existing value when dialog opens
  useEffect(() => {
    if (open && value) {
      const [hours, minutes] = value.split(':').map(Number);
      let h = 12;
      let p = 'AM';
      if (hours === 0) {
        h = 12;
        p = 'AM';
      } else if (hours <= 12) {
        h = hours;
        p = hours === 12 ? 'PM' : 'AM';
      } else {
        h = hours - 12;
        p = 'PM';
      }
      setSelectedHour(h);
      setSelectedMinute(minutes || 0);
      setAmpm(p);
      setInputHourStr(h.toString());
      setInputMinuteStr((minutes || 0).toString().padStart(2, '0'));
      setMode('hours');
      setInputMode('clock');
    } else if (open && !value) {
      // Reset to default values when opening without a value
      setSelectedHour(6);
      setSelectedMinute(0);
      setAmpm('AM');
      setInputHourStr('6');
      setInputMinuteStr('00');
      setMode('hours');
      setInputMode('clock');
    }
  }, [open, value]);

  const normalizeTime = (hour, minute, currentAmpm) => {
    let normalizedHour = hour;
    let normalizedMinute = minute;
    let normalizedAmpm = currentAmpm;

    if (isNaN(normalizedHour) || normalizedHour < 1) normalizedHour = 12;
    if (isNaN(normalizedMinute) || normalizedMinute < 0) normalizedMinute = 0;

    // Handle minute overflow
    if (normalizedMinute >= 60) {
      const extraHours = Math.floor(normalizedMinute / 60);
      normalizedMinute = normalizedMinute % 60;
      normalizedHour += extraHours;
    }

    // Handle hour overflow
    if (normalizedHour > 12) {
      normalizedHour = ((normalizedHour - 1) % 12) + 1;
    }

    return { hour: normalizedHour, minute: normalizedMinute, ampm: normalizedAmpm };
  };

  const formatTime = (hour, minute, currentAmpm) => {
    const normalized = normalizeTime(hour, minute, currentAmpm);
    let hour24 = normalized.hour;

    if (normalized.ampm === 'AM' && normalized.hour === 12) hour24 = 0;
    if (normalized.ampm === 'PM' && normalized.hour !== 12) hour24 = normalized.hour + 12;

    return `${hour24.toString().padStart(2, '0')}:${normalized.minute.toString().padStart(2, '0')}`;
  };

  const handleTimeSelect = () => {
    let h = selectedHour;
    let m = selectedMinute;

    if (inputMode === 'keyboard') {
      const parsedH = parseInt(inputHourStr, 10);
      const parsedM = parseInt(inputMinuteStr, 10);
      h = isNaN(parsedH) ? selectedHour : parsedH;
      m = isNaN(parsedM) ? selectedMinute : parsedM;
    }

    const normalized = normalizeTime(h, m, ampm);
    const timeString = formatTime(normalized.hour, normalized.minute, normalized.ampm);
    onChange(timeString);
    onOpenChange(false);
    setMode('hours');
    setInputMode('clock');
  };

  const handleCancel = () => {
    onOpenChange(false);
    setMode('hours');
    setInputMode('clock');
  };

  const getClockHandAngle = (val, max) => {
    return (val / max) * 360 - 90; // Start from top (12 o'clock)
  };

  const getNumberPosition = (val, max, radius = 76) => {
    const angle = (val / max) * 360 - 90;
    const x = Math.cos(angle * Math.PI / 180) * radius;
    const y = Math.sin(angle * Math.PI / 180) * radius;
    return { x, y };
  };

  // Smart calculate suggested AM/PM based on selected hour & date
  const suggestAmpm = useCallback((hour) => {
    try {
      const now = new Date();
      const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      let isFutureDate = false;
      if (date) {
        let parsedDate = null;
        if (date instanceof Date) {
          parsedDate = date;
        } else if (typeof date === 'string') {
          parsedDate = new Date(date);
          if (isNaN(parsedDate.getTime()) && date.includes('-')) {
            const months = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
            const parts = date.split('-');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const monthStr = parts[1].toLowerCase();
              let year = parseInt(parts[2], 10);
              if (year < 100) year += 2000;
              if (!isNaN(day) && months[monthStr] !== undefined && !isNaN(year)) {
                parsedDate = new Date(year, months[monthStr], day);
              }
            }
          }
        }

        if (parsedDate && !isNaN(parsedDate.getTime())) {
          const targetMidnight = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
          if (targetMidnight > todayMidnight) {
            isFutureDate = true;
          }
        }
      }

      if (isFutureDate) {
        if ([7, 8, 9, 10, 11].includes(hour)) {
          setAmpm('AM');
        } else {
          setAmpm('PM');
        }
      } else {
        const currentH = now.getHours();
        const currentM = now.getMinutes();
        const currentTotal = currentH * 60 + currentM;

        const hourValAM = hour === 12 ? 0 : hour;
        const hourValPM = hour === 12 ? 12 : hour + 12;

        const amTotal = hourValAM * 60 + selectedMinute;
        const pmTotal = hourValPM * 60 + selectedMinute;

        if (amTotal >= currentTotal) {
          setAmpm('AM');
        } else if (pmTotal >= currentTotal) {
          setAmpm('PM');
        } else {
          const diffAM = Math.abs(amTotal - currentTotal);
          const diffPM = Math.abs(pmTotal - currentTotal);
          setAmpm(diffAM < diffPM ? 'AM' : 'PM');
        }
      }
    } catch (err) {
      console.error('Failed to calculate proposed AM/PM:', err);
    }
  }, [date, selectedMinute]);

  const pointerDownPosRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);

  const calculateTimeFromCoordinates = useCallback((clientX, clientY, isDragMove = false, isCommit = false) => {
    if (!clockRef.current) return;
    const rect = clockRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const x = clientX - rect.left - centerX;
    const y = clientY - rect.top - centerY;

    let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;

    if (mode === 'hours') {
      const hour = Math.round(angle / 30) % 12 || 12;
      setSelectedHour(hour);
      setInputHourStr(hour.toString());
      if (isCommit) {
        suggestAmpm(hour);
        setTimeout(() => setMode('minutes'), 200);
      }
    } else {
      const rawMinute = Math.round(angle / 6) % 60;
      let minute;
      if (isDragMove || hasMovedRef.current) {
        // Dragging: 1-minute increments (0, 1, 2, ..., 59)
        minute = rawMinute;
      } else {
        // Tap/Click: snap to 5-minute multiples (0, 5, 10, ..., 55)
        minute = (Math.round(rawMinute / 5) * 5) % 60;
      }
      if (minute < 0) minute += 60;
      setSelectedMinute(minute);
      setInputMinuteStr(minute.toString().padStart(2, '0'));
    }
  }, [mode, suggestAmpm]);

  // Pointer drag event handlers for smooth interaction
  const handlePointerDown = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    pointerDownPosRef.current = { x: e.clientX, y: e.clientY };

    // Initial click/tap calculation (5-minute multiple by default on tap)
    calculateTimeFromCoordinates(e.clientX, e.clientY, false, false);

    const handlePointerMove = (moveEvent) => {
      if (!isDraggingRef.current) return;
      const dx = moveEvent.clientX - pointerDownPosRef.current.x;
      const dy = moveEvent.clientY - pointerDownPosRef.current.y;
      if (Math.hypot(dx, dy) > 4) {
        hasMovedRef.current = true;
      }
      // Dragging: 1-minute precision
      calculateTimeFromCoordinates(moveEvent.clientX, moveEvent.clientY, hasMovedRef.current, false);
    };

    const handlePointerUp = (upEvent) => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        calculateTimeFromCoordinates(upEvent.clientX, upEvent.clientY, hasMovedRef.current, true);
        hasMovedRef.current = false;
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  };

  // Keyboard input handlers
  const handleHourInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') {
      setInputHourStr('');
      return;
    }
    const val = parseInt(raw, 10);
    if (val <= 12) {
      setInputHourStr(val.toString());
      if (val >= 1 && val <= 12) {
        setSelectedHour(val);
      }
    }
  };

  const handleMinuteInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') {
      setInputMinuteStr('');
      return;
    }
    const val = parseInt(raw, 10);
    if (val < 60) {
      setInputMinuteStr(raw.slice(0, 2));
      setSelectedMinute(val);
    }
  };

  const handleHourInputBlur = () => {
    let val = parseInt(inputHourStr, 10);
    if (isNaN(val) || val < 1) val = 12;
    if (val > 12) val = 12;
    setSelectedHour(val);
    setInputHourStr(val.toString());
  };

  const handleMinuteInputBlur = () => {
    let val = parseInt(inputMinuteStr, 10);
    if (isNaN(val) || val < 0) val = 0;
    if (val > 59) val = 59;
    setSelectedMinute(val);
    setInputMinuteStr(val.toString().padStart(2, '0'));
  };

  // Keyboard ArrowUp / ArrowDown handlers for incrementing/decrementing time
  const handleHourKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const current = parseInt(inputHourStr, 10) || selectedHour || 12;
      const next = current >= 12 ? 1 : current + 1;
      setSelectedHour(next);
      setInputHourStr(next.toString());
      setTimeout(() => hourInputRef.current?.select(), 0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const current = parseInt(inputHourStr, 10) || selectedHour || 12;
      const next = current <= 1 ? 12 : current - 1;
      setSelectedHour(next);
      setInputHourStr(next.toString());
      setTimeout(() => hourInputRef.current?.select(), 0);
    }
  };

  const handleMinuteKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const current = parseInt(inputMinuteStr, 10) || selectedMinute || 0;
      const next = (current + 1) % 60;
      setSelectedMinute(next);
      setInputMinuteStr(next.toString().padStart(2, '0'));
      setTimeout(() => minuteInputRef.current?.select(), 0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const current = parseInt(inputMinuteStr, 10) || selectedMinute || 0;
      const next = current === 0 ? 59 : current - 1;
      setSelectedMinute(next);
      setInputMinuteStr(next.toString().padStart(2, '0'));
      setTimeout(() => minuteInputRef.current?.select(), 0);
    }
  };

  const hourNumbers = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minuteNumbers = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const handleHourInputFocus = (e) => {
    setMode('hours');
    setActiveInput('hours');
    e.target.select();
  };

  const handleMinuteInputFocus = (e) => {
    setMode('minutes');
    setActiveInput('minutes');
    e.target.select();
  };

  const hourInputRef = useRef(null);
  const minuteInputRef = useRef(null);

  // Auto-select and focus when activeInput or inputMode changes
  useEffect(() => {
    if (inputMode === 'keyboard') {
      const timer = setTimeout(() => {
        if (activeInput === 'hours' && hourInputRef.current) {
          hourInputRef.current.focus();
          hourInputRef.current.select();
        } else if (activeInput === 'minutes' && minuteInputRef.current) {
          minuteInputRef.current.focus();
          minuteInputRef.current.select();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [inputMode, activeInput]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-lg p-0 fixed top-1/2 left-1/2 !transform -translate-x-1/2 -translate-y-1/2 z-50 w-[300px] h-auto max-h-[95vh] sm:max-w-[800px] sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] border-none outline-none shadow-none overflow-hidden">
        <div className="p-0">
          {/* Header with time display */}
          <div className="bg-sahi-primary-color text-white py-4 text-center">
            {inputMode === 'clock' ? (
              <div className="text-4xl font-light tracking-wide">
                <span
                  className={`cursor-pointer ${mode === 'hours' ? 'text-white' : 'text-white/60'}`}
                  onClick={() => {
                    setMode('hours');
                    setActiveInput('hours');
                  }}
                >
                  {selectedHour}
                </span>
                <span className="mx-2 text-white/60">:</span>
                <span
                  className={`cursor-pointer ${mode === 'minutes' ? 'text-white' : 'text-white/60'}`}
                  onClick={() => {
                    setMode('minutes');
                    setActiveInput('minutes');
                  }}
                >
                  {selectedMinute.toString().padStart(2, '0')}
                </span>
                <span className="ml-2 text-lg text-white/60">{ampm}</span>
              </div>
            ) : (
              <div className="text-lg font-medium tracking-wide py-1">
                Enter Time
              </div>
            )}
          </div>

          {/* Clock Face OR Keyboard Input */}
          {inputMode === 'clock' ? (
            <div className="py-6 bg-white flex justify-center">
              <div
                ref={clockRef}
                className="relative w-52 h-52 bg-gray-100 rounded-full cursor-pointer touch-none select-none"
                onPointerDown={handlePointerDown}
              >
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 208 208">
                  <circle cx="104" cy="104" r="100" fill="#f3f4f6" stroke="none" />
                  {(mode === 'hours' ? hourNumbers : minuteNumbers).map((number, index) => {
                    const pos = getNumberPosition(mode === 'hours' ? number : index * 5, mode === 'hours' ? 12 : 60, 78);
                    const isSelected = mode === 'hours' ? selectedHour === number : selectedMinute === number;
                    return (
                      <g key={number}>
                        <circle
                          cx={104 + pos.x}
                          cy={104 + pos.y}
                          r="15"
                          fill={isSelected ? "var(--sahi-primary-color)" : "transparent"}
                          className="transition-all duration-200"
                        />
                        <text
                          x={104 + pos.x}
                          y={104 + pos.y + 5}
                          textAnchor="middle"
                          className={`text-base font-normal select-none transition-all duration-200 ${isSelected ? 'fill-white' : 'fill-[#0D1F2D]'}`}
                          style={{ fill: isSelected ? 'white' : "var(--sahi-primary-color)" }}
                        >
                          {mode === 'hours' ? number : number.toString().padStart(2, '0')}
                        </text>
                      </g>
                    );
                  })}
                  {mode === 'minutes' && selectedMinute % 5 !== 0 && (
                    <g>
                      {(() => {
                        const pos = getNumberPosition(selectedMinute, 60, 78);
                        return (
                          <>
                            <circle
                              cx={104 + pos.x}
                              cy={104 + pos.y}
                              r="15"
                              fill="var(--sahi-primary-color)"
                              className="transition-all duration-200"
                            />
                            <text
                              x={104 + pos.x}
                              y={104 + pos.y + 5}
                              textAnchor="middle"
                              className="text-base font-normal select-none fill-white"
                            >
                              {selectedMinute.toString().padStart(2, '0')}
                            </text>
                          </>
                        );
                      })()}
                    </g>
                  )}
                  <g>
                    <line
                      x1="104"
                      y1="104"
                      x2={104 + Math.cos((getClockHandAngle(mode === 'hours' ? selectedHour : selectedMinute, mode === 'hours' ? 12 : 60)) * Math.PI / 180) * (mode === 'hours' ? 45 : 60)}
                      y2={104 + Math.sin((getClockHandAngle(mode === 'hours' ? selectedHour : selectedMinute, mode === 'hours' ? 12 : 60)) * Math.PI / 180) * (mode === 'hours' ? 45 : 60)}
                      stroke="var(--sahi-primary-color)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      className="transition-all duration-300"
                    />
                    <circle cx="104" cy="104" r="4" fill="var(--sahi-primary-color)" />
                  </g>
                </svg>
              </div>
            </div>
          ) : (
            <div className="py-8 bg-white flex flex-col items-center justify-center px-6">
              <div className="flex items-center gap-3">
                {/* Hour Input Block */}
                <div className="flex flex-col items-center">
                  <input
                    ref={hourInputRef}
                    type="tel"
                    maxLength={2}
                    value={inputHourStr}
                    onChange={handleHourInputChange}
                    onKeyDown={handleHourKeyDown}
                    onFocus={handleHourInputFocus}
                    onClick={(e) => e.target.select()}
                    onBlur={handleHourInputBlur}
                    className={cn(
                      "w-16 h-14 text-center text-2xl font-normal bg-zinc-100 text-zinc-900 rounded-lg outline-none border transition-all cursor-text",
                      activeInput === 'hours' ? "border-sahi-primary-color ring-2 ring-sahi-primary-color/20 bg-white" : "border-zinc-200"
                    )}
                  />
                  <span className="text-sm text-text-color mt-1.5 font-medium">Hour</span>
                </div>

                <span className="text-3xl font-light text-text-color pb-5">:</span>

                {/* Minute Input Block */}
                <div className="flex flex-col items-center">
                  <input
                    ref={minuteInputRef}
                    type="tel"
                    maxLength={2}
                    value={inputMinuteStr}
                    onChange={handleMinuteInputChange}
                    onKeyDown={handleMinuteKeyDown}
                    onFocus={handleMinuteInputFocus}
                    onClick={(e) => e.target.select()}
                    onBlur={handleMinuteInputBlur}
                    className={cn(
                      "w-16 h-14 text-center text-2xl font-normal bg-zinc-100 text-zinc-900 rounded-lg outline-none border transition-all cursor-text",
                      activeInput === 'minutes' ? "border-sahi-primary-color ring-2 ring-sahi-primary-color/20 bg-white" : "border-zinc-200"
                    )}
                  />
                  <span className="text-sm text-text-color mt-1.5 font-medium">Minute</span>
                </div>
              </div>
            </div>
          )}

          {/* AM/PM Toggle and Keyboard Mode Switch */}
          <div className="pb-4 relative flex items-center justify-center">
            {/* Keyboard / Clock Toggle Button on far left */}
            <div className="absolute left-6">
              <button
                type="button"
                onClick={() => {
                  if (inputMode === 'clock') {
                    setInputMode('keyboard');
                    setInputHourStr(selectedHour.toString());
                    setInputMinuteStr(selectedMinute.toString().padStart(2, '0'));
                  } else {
                    handleHourInputBlur();
                    handleMinuteInputBlur();
                    setInputMode('clock');
                  }
                }}
                className="p-2 rounded-full text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors cursor-pointer"
                title={inputMode === 'clock' ? "Type time directly" : "Use clock dial"}
              >
                {inputMode === 'clock' ? (
                  <Keyboard className="h-5 w-5" />
                ) : (
                  <Clock className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* AM / PM Buttons */}
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setAmpm('AM')}
                className={`w-10 h-10 rounded-full text-sm font-medium transition-all duration-200 ${ampm === 'AM' ? 'bg-sahi-primary-color text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                AM
              </button>
              <button
                type="button"
                onClick={() => setAmpm('PM')}
                className={`w-10 h-10 rounded-full text-sm font-medium transition-all duration-200 ${ampm === 'PM' ? 'bg-sahi-primary-color text-white' : 'bg-gray-200 text-gray-700'}`}
              >
                PM
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between px-6 pb-4">
            <button
              type="button"
              onClick={handleCancel}
              className="text-[#0D1F2D] text-sm font-medium tracking-wide hover:text-[#0D1F2D]/80 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleTimeSelect}
              className="text-[#0D1F2D] text-sm font-medium tracking-wide hover:text-[#0D1F2D]/80 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Updated Time Input Component with zoom-responsive layout
const TimeInputWithDialog = ({ value, onChange, date = null, placeholder = "Select time", required = false, label, fieldId, disabled = false, isMobile = false, hideLabel = false, canClose = false, onClose }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const displayTime = (timeValue) => {
    if (!timeValue) return '';

    const [hours, minutes] = timeValue.split(':').map(Number);
    let displayHour = hours;
    let ampm = 'AM';

    if (hours === 0) {
      displayHour = 12;
      ampm = 'AM';
    } else if (hours === 12) {
      displayHour = 12;
      ampm = 'PM';
    } else if (hours > 12) {
      displayHour = hours - 12;
      ampm = 'PM';
    }

    return `${displayHour.toString().padStart(2, '0')}:${minutes.toString
      ().padStart(2, '0')} ${ampm}`;
  };

  const handleCloseClick = (e) => {
    e.stopPropagation(); // prevent opening dialog
    e.preventDefault();
    if (disabled) return;

    onClose?.();
  };


  return (
    <>
      <div className="space-y-1.5">
        {isMobile == true ? (
          <>
            {label ? (
              <Label
                htmlFor={fieldId}
                required={required}
              >
                {label}
              </Label>
            ) : null}
          </>
        ) : hideLabel == true ? null : <div className="min-h-[20px]">
          {label ? (
            <Label
              htmlFor={fieldId}
              required={required}
            >
              {label}
            </Label>
          ) : null}
        </div>}

        <Button
          onClick={() => !disabled && setIsDialogOpen(true)}
          disabled={disabled}
          className="h-11 w-full text-sm sahi-input font-normal rounded-md transition-all duration-200 !gap-0 text-left flex items-center justify-between overflow-hidden p-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {/* Text area with responsive padding and proper flex behavior */}
          <span className={cn(
            "flex-1 min-w-0 px-2 sm:px-3", // Responsive padding, min-w-0 allows shrinking
            value ? "text-gray-900" : "text-muted-foreground"
          )}>
            <span className="block truncate"> {/* Ensures text truncates instead of overflowing */}
              {value ? displayTime(value) : placeholder}
            </span>
          </span>

          {/* Icon area with responsive sizing and flexible width */}
          <span className={cn(
            "flex-shrink-0 h-full inline-flex items-center justify-center",
            "px-2 sm:px-3 bg-white border-l border-gray-300", // Responsive padding
            "min-w-[1.5rem] sm:min-w-[1.5rem]", // Responsive minimum width
            disabled && "opacity-50"
          )}>
            <Clock className="h-4 w-4 text-gray-700 flex-shrink-0" />
          </span>

          {/* {canClose && (
            <button
              type="button" // IMPORTANT: Prevents form submission
              onClick={handleCloseClick}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={disabled}
              className={cn(
                "flex-shrink-0 h-full inline-flex items-center justify-center",
                "px-2 sm:px-3 bg-bg-grey border-l border-gray-300 bg-white ",
                "min-w-[1.5rem] sm:min-w-[1.5rem]",
                "cursor-pointer transition-colors",
                disabled && "opacity-50 pointer-events-none"
              )}
            >
              <X className="h-4 w-4 text-gray-700 flex-shrink-0" />
            </button>
          )} */}

        </Button>
      </div>

      <TimePickerDialog
        open={isDialogOpen && !disabled}
        onOpenChange={setIsDialogOpen}
        value={value}
        onChange={onChange}
        date={date}
        fieldId={fieldId}
        label={label}
      />
    </>
  );
};

export { TimePickerDialog, TimeInputWithDialog };